import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridListViewColDef, GridColDef, GridRenderCellParams, Toolbar } from '@mui/x-data-grid-pro';
import { csCZ, huHU, roRO, skSK, hrHR, enUS, itIT } from '@mui/x-data-grid/locales';
import { Box, Stack, Typography } from '@mui/material';

import { Refuel } from '@/tmp';
import { fulldate, dateYMD } from '@/utilities/dateUtils';
import { SearchComponent } from '@/components/common/Search';
import { ctxRefuel } from '@/contexts/RefuelsCtx';
import { flagsWithGeoAdressString } from '@/components/common/FlagsWithGeoAddr';
import { isRunningInApp } from '@/utilities/sizeUtils';
import DisplayNoResult from '../common/DisplayNoResult';

import './RefuelStyle.css';
import { ToolbarSortItem } from '../common/ToolbarSortItem';
import GridExport from '../common/GridExport';

export type RefuelDataType = {
  id: number;
  refuelDate: string;
  refuelPlace: string;
  startFuelLevel: number;
  stopFuelLevel: number;
  amountRefuels: number;
};

export function RefuelData({ refuel, isListView }: { refuel: Refuel[] | undefined; isListView: boolean }) {
  const ctx = useContext(ctxRefuel);
  const { t, i18n } = useTranslation();

  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [fuelTemplate, setFuelTemplate] = useState<RefuelDataType[]>([]);
  const [prevSelectedTarget, setPrevSelectedTarget] = useState(ctx?.selectedTarget);

  useEffect(() => {
    if (ctx?.selectedTarget !== prevSelectedTarget) {
      setSearchText(undefined);
      setPrevSelectedTarget(ctx?.selectedTarget);
    }
  }, [ctx?.selectedTarget, prevSelectedTarget]);

  // FIXME a Localization-t nem tudja importalni :(
  const localeMap: Record<string, typeof enUS> = {
    en: enUS,
    hu: huHU,
    ro: roRO,
    sk: skSK,
    cz: csCZ,
    hr: hrHR,
    sr: enUS,
    sl: enUS,
    it: itIT,
  };

  const columns = [
    {
      field: 'refuelDate',
      headerName: t('refuels.table.header.refuelDate'),
      flex: 1,
      type: 'string',
      sortable: true,
    },
    {
      field: 'refuelPlace',
      headerName: t('refuels.table.header.refuelPlace'),
      flex: 1,
      type: 'string',
      renderCell: (params: GridRenderCellParams) => params.value,
      sortable: true,
    },
    {
      field: 'startFuelLevel',
      headerName: t('refuels.table.header.startFuelLevel'),
      flex: 1,
      align: 'left',
      type: 'string',
      valueFormatter: (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(1)),
      sortable: true,
    },
    {
      field: 'amountRefuels',
      headerName: t('refuels.table.header.amountRefuels'),
      flex: 1,
      type: 'string',
      align: 'left',
      valueFormatter: (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(1)),
      sortable: true,
    },
    {
      field: 'stopFuelLevel',
      headerName: t('refuels.table.header.stopFuelLevel'),
      flex: 1,
      type: 'string',
      align: 'left',
      valueFormatter: (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(1)),
      sortable: true,
    },
  ] satisfies GridColDef[];

  function ListViewCell(params: GridRenderCellParams<RefuelDataType>) {
    return (
      <Stack direction="row" sx={{ width: '100%', alignItems: 'center', height: '100%', gap: 3 }}>
        <Stack sx={{ flexGrow: 1 }}>
          <Typography data-qa-element-id="refuel_place_label" variant="body2" fontWeight={600} fontSize="16px">
            {params.row.refuelPlace}
          </Typography>
          <Typography data-qa-element-id="refuel_date_label" variant="body2" color="text.secondary">
            {params.row.refuelDate}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              rowGap: 0.5,
              mt: 1,
              textAlign: 'center',
            }}
          >
            {/* Row 1: Labels */}
            <Typography variant="body2" data-qa-element-id="start_fuel_label">
              {t('refuels.table.header.startFuelLevel')}
            </Typography>
            <Typography variant="body2" data-qa-element-id="refuel_amount_label">
              {t('refuels.table.header.amountRefuels')}
            </Typography>
            <Typography variant="body2" data-qa-element-id="stop_fuel_label">
              {t('refuels.table.header.stopFuelLevel')}
            </Typography>

            {/* Row 2: Numbers */}
            <Typography variant="body2" fontWeight={600} data-qa-element-id="start_fuel_value">
              {params.row.startFuelLevel.toFixed(1)}
            </Typography>
            <Typography variant="body2" fontWeight={600} data-qa-element-id="refuel_amount_value">
              {params.row.amountRefuels.toFixed(1)}
            </Typography>
            <Typography variant="body2" fontWeight={600} data-qa-element-id="stop_fuel_value">
              {params.row.stopFuelLevel.toFixed(1)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    );
  }

  const listViewColDef: GridListViewColDef<RefuelDataType> = {
    field: 'listView',
    renderCell: ListViewCell,
  };

  const mapRefuelToRow = (row: Refuel, index: number) =>
    ({
      id: index,
      refuelDate: fulldate(row.fuelFillingTimeMicros, t),
      refuelPlace: flagsWithGeoAdressString({
        countryCode: row.countryCode,
        geocodedAddress: row.fuelFillingPosition,
      }),
      startFuelLevel: row.fillStartLevel,
      amountRefuels: row.fillAmount,
      stopFuelLevel: row.fillEndLevel,
    }) satisfies RefuelDataType;

  useEffect(() => {
    if (!refuel) return;

    const formattedRows = refuel.map((row, index) => mapRefuelToRow(row, index));

    if (!searchText) {
      setFuelTemplate(formattedRows);
    } else {
      const filtered = formattedRows.filter((displayRow) => {
        return Object.values(displayRow).some((value) =>
          value.toString().toLowerCase().includes(searchText.toLowerCase())
        );
      });

      setFuelTemplate(filtered);
    }
  }, [searchText, refuel]);

  if (!ctx) return null;

  function createFileName() {
    if (ctx?.selectedTarget?.name && ctx?.selectedDates?.from && ctx?.selectedDates?.to) {
      const targetName = ctx?.selectedTarget?.name.replace(/ /g, '_');
      return targetName + '_' + dateYMD(ctx?.selectedDates?.from) + '_' + dateYMD(ctx?.selectedDates?.to);
    } else {
      return 'iTrack';
    }
  }

  function CustomToolbar() {
    const filename = createFileName();

    return (
      <div style={{ padding: '5px', display: 'flex', flexDirection: 'row-reverse' }}>
        <Toolbar>
          {!isRunningInApp() && <GridExport filename={filename} isListView={isListView} />}
          {isListView && <ToolbarSortItem listView={isListView} />}
        </Toolbar>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <SearchComponent
        searchText={searchText}
        setSearchText={setSearchText}
        placeholder={t('refuels.fuelSearchPlaceHolder')}
      />
      <DataGridPro
        sortingOrder={['asc', 'desc']}
        rows={fuelTemplate}
        columns={columns}
        autoHeight={isListView}
        listView={isListView}
        listViewColumn={listViewColDef}
        autosizeOptions={{
          columns: ['refuelDate', 'refuelPlace', 'startFuelLevel', 'stopFuelLevel', 'amountRefuels'],
        }}
        getRowHeight={() => 'auto'}
        hideFooterRowCount={ctx.loading || isListView}
        disableRowSelectionOnClick
        disableColumnMenu
        disableDensitySelector
        pageSizeOptions={[50, 100]}
        localeText={{
          ...localeMap[i18n.language].components.MuiDataGrid.defaultProps.localeText,
          noRowsLabel: t('refuels.noResult.noResultDateInterval'),
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => <DisplayNoResult />,
        }}
        getRowSpacing={() => ({
          bottom: 10,
        })}
        sx={{
          fontSize: '15px',
          '& .MuiDataGrid-cell': { paddingTop: '10px' },
          '& .MuiDataGrid-main': { minHeight: '300px' },
        }}
        loading={ctx.loading}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
          noRowsOverlay: {
            id: 'no_result_label',
          },
          main: {
            'data-testid': 'refuels_table',
          },
          columnHeaders: {
            id: 'refuels_table_column_header',
          },
          footerRowCount: {
            id: 'row_count_label',
          },
          panel: {
            id: 'filter_panel',
          },
        }}
        showToolbar
      />
    </div>
  );
}
